import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { selectUsersFullPaginatedData } from 'src/app/Budgets/redux/budgets.selectors'
import {
  selectBudgetRequestCountByRole,
  selectBudgetRequestPaginationByRole,
  selectBudgetRequestRole,
  selectBudgetRequestsByRole,
  selectFetchingBudgetRequests,
} from 'src/app/BudgetsRequests/redux/budgetRequest.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetRequestsBoxWithFilters from 'src/components/BudgetRequestsBoxWithFilters/BudgetRequestsBoxWithFilters'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { PusherEvents } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import usePusher from 'src/hooks/usePusher'
import { useTranslate } from 'src/localization/Trns'
import { BudgetRequestStatus } from 'src/models/budgetRequest.models'
import {
  getBudgetRequests,
  removeBudgetRequest,
} from 'src/redux/actions/data/budgetRequests.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const BudgetsRequestContent: React.FC = () => {
  const translate = useTranslate()

  const role = useSelector(selectBudgetRequestRole)
  const budgetRequests = useSelector(selectBudgetRequestsByRole(role))
  const budgetRequestCount = useSelector(selectBudgetRequestCountByRole(role))
  const budgetRequestsLoading = useSelector(selectFetchingBudgetRequests)
  const budgetRequestPagination = useSelector(
    selectBudgetRequestPaginationByRole(role),
  )
  const appName = useSelector(selectAppName)
  const user = useCurrentUser()
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const { bind } = usePusher(user?.id)
  const [loading, setLoading] = useState(false)
  const dispatch = useBackgroundDispatch()

  bind(PusherEvents.BudgetRequest, (data: { id: string; action: string }) => {
    if (data.action === 'UPDATED') {
      dispatch(removeBudgetRequest(data.id))
    }
    fetchBudgetRequests()
  })

  const fetchBudgetRequests = (page?: number): void => {
    dispatch(
      getBudgetRequests({
        [role]: 'me',
        count: 10,
        page,
        statuses: [BudgetRequestStatus.Pending],
      }),
    )
  }

  const infiniteScrollProps = {
    ...budgetRequests,
    loading: budgetRequestsLoading,
    page: budgetRequestPagination?.page,
    // TODO: Do this in the reducer
    complete:
      budgetRequestPagination.numberOfPages === 0 ||
      budgetRequestPagination.page >= budgetRequestPagination.numberOfPages - 1,
    fetch: fetchBudgetRequests,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers({
        q: filterInput,
        count: 10,
        page: page,
      }),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  useEffect(() => {
    if (budgetRequestCount) {
      setLoading(false)
    }
  }, [budgetRequestCount])

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  return loading && userInfiniteScrollProps ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Budget Requests | ${appName}`}</title>
        <meta
          name="description"
          content={`Budget Requests page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent noPadding>
          <BudgetRequestsBoxWithFilters
            budgetRequests={budgetRequests}
            isSlim={false}
            totalBudgetRequests={
              budgetRequestCount !== undefined ? budgetRequestCount : 0
            }
            defaultTitle={
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate(
                  role === 'approver'
                    ? budgetRequestCount === 1
                      ? '${count} Request'
                      : '${count} Requests'
                    : '${count} Pending',
                  {
                    count: `${budgetRequestCount}`,
                  },
                )}
              </span>
            }
            infiniteScrollProps={infiniteScrollProps}
            userInfiniteScrollProps={userInfiniteScrollProps}
            isApprover={role === 'approver'}
            clientHeight={clientHeight || 0}
          />
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default BudgetsRequestContent
